import { useState } from 'react';
import { motion } from 'framer-motion';
import { PlayDiagram } from '@/components/PlayDiagram';
import type { DiagramData } from '@/data/lessons';

interface DiagramIdentifyQuestionProps {
  question: string;
  diagram: DiagramData;
  options: string[];
  correctIndex: number;
  onAnswer: (index: number) => void;
  selectedAnswer: number | null;
  isCorrect: boolean | null;
}

export function DiagramIdentifyQuestion({
  question,
  diagram,
  options,
  correctIndex,
  onAnswer,
  selectedAnswer,
  isCorrect,
}: DiagramIdentifyQuestionProps) {
  return (
    <div className="w-full space-y-5">
      <div className="text-center">
        <span className="inline-block bg-accent/20 text-accent-foreground font-display font-bold text-xs px-3 py-1 rounded-full mb-3">
          🔍 IDENTIFY THE CONCEPT
        </span>
      </div>

      {/* Prominent diagram */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative"
      >
        <div className="absolute -inset-1 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl blur-sm" />
        <div className="relative">
          <PlayDiagram diagram={diagram} />
        </div>
      </motion.div>

      <p className="font-body font-semibold text-lg text-center leading-snug">
        {question}
      </p>

      {/* Card-style options in a 2x2 grid */}
      <div className="grid grid-cols-2 gap-2.5">
        {options.map((option, i) => {
          let variant = 'bg-card border-border hover:border-primary/50';
          if (selectedAnswer !== null) {
            if (i === correctIndex) {
              variant = 'bg-success/10 border-success ring-2 ring-success/30';
            } else if (i === selectedAnswer && !isCorrect) {
              variant = 'bg-destructive/10 border-destructive ring-2 ring-destructive/30';
            } else {
              variant = 'bg-muted/50 border-border opacity-50';
            }
          }

          return (
            <motion.button
              key={i}
              onClick={() => onAnswer(i)}
              disabled={selectedAnswer !== null}
              whileTap={selectedAnswer === null ? { scale: 0.95 } : {}}
              className={`p-4 rounded-xl border-2 text-center font-body font-semibold text-sm transition-all ${variant}`}
            >
              {option}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
